// Risk trend sparkline / line chart used by UserDetailPage (email prop) and
// MetaDetailPage (scope prop -> the server 'risk_trend' for the slice). Both
// plot the SERVER's stored daily score history (risk_snapshot): the user path
// via 'user_risk_trend' - real points that only exist from the user's first
// scored day, so a new user shows no phantom pre-join months and the newest
// point equals the roster score. Never the browser model (BD-1245). Dense daily
// history is downsampled to a readable set of markers. The chart hides
// entirely below 2 real points.
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MainCard from '../../../components/cards/MainCard';
import { motion } from 'motion/react';
import { bandFor } from '../riskScore';
import WidgetState from './WidgetState';
import useDashboardData from '../hooks/useDashboardData';
import useFeature from '../useFeature';
import { useRiskBenchmarkQuery } from './summary/BenchmarkPositionCard';

const fmtDayLabel = (iso) => new Date(iso + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

// Evenly spaced indices (always including the first and last) used to place
// markers and axis labels on a dense daily series. The LINE itself is drawn
// through every day - thinning the plotted points made a month of history read
// as "only 8 days" on the axis, which is what it looked like to reviewers.
function tickIndices(length, maxTicks = 6) {
    if (length <= maxTicks) return Array.from({ length }, (_, i) => i);
    const out = [];
    for (let i = 0; i < maxTicks; i++) {
        out.push(Math.round((i * (length - 1)) / (maxTicks - 1)));
    }
    return [...new Set(out)];
}

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };

// Smooth the line through the real data points with a Catmull-Rom spline
// (converted to cubic beziers). The score is a rolling 180-day window, so
// consecutive monthly points overlap by ~5 months and the true path between
// them is gentle; smoothing reflects that continuity without the cost of
// recomputing the score at weekly resolution.
function smoothPath(pts) {
    if (pts.length < 2) return pts.length ? `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}` : '';
    let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return d;
}

// Compact 6-month sparkline of this user's score, with an industry-average
// reference dashed across. Anchor + action are surfaced in the modal, here
// we provide visual context so the user-detail page satisfies the spec's
// "individual risk score visible on every relevant user-level surface".
export default function UserRiskTrendChart({ email, scope }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const benchmarkOn = useFeature('industryBenchmarking');
    const trendQ = useDashboardData({ metric: 'users', aggregation: 'user_risk_trend', field: email }, { skip: !email || !!scope });
    // BD-1245: 6 months, like a user's own history and the Risk page's trend.
    const groupQ = useDashboardData({ metric: 'simulations', aggregation: 'risk_trend', period: '6m', scope }, { skip: !scope });
    // Industry reference line from the server benchmark (the tenancy's industry).
    const benchQ = useRiskBenchmarkQuery(scope?.tenancy ? { tenancy: scope.tenancy } : undefined, { skip: !benchmarkOn });
    const industryAvg = benchQ.data?.riskBenchmark?.industryAvg ?? null;
    const data = useMemo(() => {
        const pts = (scope ? groupQ.data?.riskTrend : trendQ.data?.riskTrend);
        return Array.isArray(pts) ? pts.map((p) => ({ label: fmtDayLabel(p.day), score: p.score })) : [];
    }, [scope, trendQ.data, groupQ.data]);

    const currentScore = data[data.length - 1]?.score ?? 0;
    const earliest = data[0]?.score ?? currentScore;
    const delta = currentScore - earliest;
    const band = bandFor(currentScore);
    const deltaColor = delta > 0 ? '#FF5252' : delta < 0 ? '#66BB6A' : 'text.secondary';

    const showIndustry = benchmarkOn && industryAvg != null;
    const compareValues = showIndustry ? [industryAvg] : [];
    const min = Math.max(0, Math.min(...data.map((p) => p.score), ...compareValues) - 6);
    const max = Math.min(100, Math.max(...data.map((p) => p.score), ...compareValues) + 6);
    const range = max - min || 1;

    // Measure the plot area so the SVG fills whatever space the card gives it
    // (the card stretches to match its row neighbour). Drawing in real pixel
    // coordinates keeps markers circular and strokes crisp.
    //
    // The measured element must not be able to grow because of what we draw in
    // it, or measuring feeds back into layout: the SVG is rendered at the
    // measured height, that height is added to the labels below it, the box
    // grows, the observer fires with the bigger height, and the card expands a
    // little further on every frame. That is what "the page just kept
    // expanding" was. The SVG is therefore absolutely positioned inside a
    // relative box, so it contributes nothing to layout, and sub-pixel
    // measurement noise is ignored.
    const chartRef = useRef(null);
    const [size, setSize] = useState({ w: 540, h: 104 });
    useEffect(() => {
        const el = chartRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            const r = entries[0]?.contentRect;
            if (!r || r.width <= 0 || r.height <= 0) return;
            setSize((prev) => (Math.abs(prev.w - r.width) < 2 && Math.abs(prev.h - r.height) < 2
                ? prev
                : { w: r.width, h: r.height }));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // No points: the user or slice has no scored days yet.
    if (data.length === 0) {
        const qs = [trendQ, groupQ];
        if (!qs.some((q) => q.isFetching || q.isError)) return null;
        return (
            <WidgetState queries={qs}>
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', minHeight: 160 }} />
            </WidgetState>
        );
    }

    const LABEL_H = 0; // labels render as HTML beneath the plot, not inside the SVG
    const W = Math.max(120, size.w);
    const H = Math.max(60, size.h - LABEL_H);
    const stepX = data.length > 1 ? W / (data.length - 1) : W;
    const points = data.map((p, i) => ({
        x: i * stepX,
        y: H - ((p.score - min) / range) * H,
        score: p.score,
        label: p.label,
    }));
    // Markers and axis labels are thinned; the line keeps every day.
    const ticks = tickIndices(points.length);
    const path = smoothPath(points);
    const areaPath = `${path} L ${W} ${H} L 0 ${H} Z`;
    const industryY = showIndustry ? H - ((industryAvg - min) / range) * H : 0;

    return (
        <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <WidgetState queries={[trendQ, groupQ]}>
                <Box sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'stretch', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 140, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                            Risk score trend
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 1 }}>
                            {`Rolling 180-day (6-month) metric · ${data[0]?.label} - ${data[data.length - 1]?.label} · ${data.length} ${data.length === 1 ? 'day' : 'days'} of daily scores`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                            <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: band?.color || 'text.primary', lineHeight: 1 }}>
                                {currentScore}
                            </Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: deltaColor }}>
                                {delta > 0 ? '↑' : delta < 0 ? '↓' : '→'} {Math.abs(delta)} pts since {data[0]?.label}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 240, minHeight: 96, alignSelf: 'stretch', display: 'flex', flexDirection: 'column' }}>
                        <Box ref={chartRef} sx={{ position: 'relative', flex: 1, minHeight: 60 }}>
                        <Box component="svg" viewBox={`0 0 ${W} ${H + LABEL_H}`} preserveAspectRatio="none" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}>
                            {/* Industry reference line, only when benchmarking is on */}
                            {showIndustry && (
                                <>
                                    <line
                                        x1={0} x2={W} y1={industryY} y2={industryY}
                                        stroke="#6B7591" strokeWidth="1" strokeDasharray="4 4" opacity={0.6}
                                    />
                                    <text x={W} y={industryY - 4} textAnchor="end" fontSize="9" fontFamily="Inter,sans-serif" fill={theme.palette.text.primary}>
                                        industry {industryAvg}
                                    </text>
                                </>
                            )}
                            {/* Area + line */}
                            <path d={areaPath} fill={band?.color || '#FF5252'} opacity={isDark ? 0.10 : 0.08} />
                            <path d={path} fill="none" stroke={band?.color || '#FF5252'} strokeWidth="2" strokeLinejoin="round" />
                            {/* Markers + month labels */}
                            {ticks.map((i) => {
                                const p = points[i];
                                return (
                                    <circle
                                        key={i}
                                        cx={p.x} cy={p.y} r="3"
                                        fill={band?.color || '#FF5252'}
                                        stroke={theme.palette.background.paper} strokeWidth="1.5"
                                    />
                                );
                            })}
                        </Box>
                        </Box>
                        {/* Axis labels live outside the SVG: the plot is drawn with
                            preserveAspectRatio="none", which stretches any text inside it. */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25, gap: 1 }}>
                            {ticks.map((idx, i) => (
                                <Typography
                                    key={`${points[idx].label}-${idx}`}
                                    sx={{
                                        fontSize: 9, color: 'text.primary', whiteSpace: 'nowrap',
                                        flex: '1 1 0', minWidth: 0,
                                        textAlign: i === 0 ? 'left' : i === ticks.length - 1 ? 'right' : 'center',
                                    }}
                                >
                                    {points[idx].label}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                </Box>
                </WidgetState>
            </MainCard>
        </motion.div>
    );
}
